import { Box, CircularProgress, Typography, useTheme } from '@mui/material';
import { memo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { actions } from 'services/store/remixer';
import {
  selectEffectsOrder,
  selectEffectValue,
} from 'services/store/remixer/selectors';
import { Effects } from 'services/store/remixer/types';
import Knob from './components/Knob';
import useEffectsChain from './hooks/useEffectsChain';

const EffectControl = ({ effect }: { effect: Effects }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const theme = useTheme();
  const effectValue = useSelector(selectEffectValue(effect));

  const onChange = useCallback(
    (value: number) => {
      dispatch(actions.setEffectValue({ effect, value }));
    },
    [dispatch, effect]
  );

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        borderRadius: theme.spacing(1),
        padding: theme.spacing(2),
        background: theme.palette.grey[800],
      }}
    >
      <Typography
        sx={{
          mb: theme.spacing(2),
          color: theme.palette.text.primary,
        }}
      >
        {t(`remixer:effects:${effect}`)}
      </Typography>
      <Knob value={effectValue} onChange={onChange} />
    </Box>
  );
};

const MemoizedEffectControl = memo(EffectControl);

const EffectsPanel = () => {
  const { isReady } = useEffectsChain();
  const theme = useTheme();

  const effectsOrder = useSelector(selectEffectsOrder);

  if (!isReady) {
    return (
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <CircularProgress color="primary" size={theme.spacing(8)} />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        gap: theme.spacing(1),
        [theme.breakpoints.down('md')]: {
          flexWrap: 'wrap',
        },
      }}
    >
      {effectsOrder.map((effect) => (
        <Box
          key={effect}
          sx={{
            width: '20%',
            [theme.breakpoints.down('md')]: {
              width: '40%',
            },
            [theme.breakpoints.down('sm')]: {
              width: '100%',
            },
          }}
        >
          <MemoizedEffectControl effect={effect} />
        </Box>
      ))}
    </Box>
  );
};

export default EffectsPanel;
